import React from 'react';
import { CartLine, CartState, DealCartLine, MenuItem, DrinkSelection, cartLinePrice } from './cartTypes';

export type CartApi = {
  state: CartState;
  count: number;
  subtotal: number;
  discount: number;
  total: number;
  addItem: (
    item: MenuItem,
    quantity?: number,
    drinkSelection?: DrinkSelection,
    notes?: string,
    meal?: { isMeal: boolean; mealUpgradePrice: number },
    saladSaucePreference?: { value: string; label: string },
    flavourChoice?: { value: string; label: string }
  ) => void;
  addDeal: (payload: Omit<DealCartLine, 'kind' | 'lineId'>) => void;
  removeLine: (lineId: string) => void;
  removeItem: (lineId: string) => void;
  setQuantity: (lineId: string, quantity: number) => void;
  setLineNotes: (lineId: string, notes: string) => void;
  clear: () => void;
  applyPromoCode: (code: string) => Promise<{ error?: string; code?: string; discountAmount?: number }>;
  clearPromoCode: () => void;
};

export const CartContext = React.createContext<CartApi | undefined>(undefined);

export function useCart(): CartApi {
  const ctx = React.useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

export function cartTotals(lines: CartLine[]): { count: number; subtotal: number } {
  const count = lines.reduce((acc, l) => acc + l.quantity, 0);
  const subtotal = lines.reduce((acc, l) => acc + l.quantity * cartLinePrice(l), 0);
  return { count, subtotal };
}

export function isLineDiscountEligible(line: CartLine): boolean {
  if (line.kind === 'item') {
    return line.item.eligibleForDiscount !== false;
  }
  return line.eligibleForDiscount === true;
}

/** Subtotal of cart lines marked eligible for promo codes. */
export function discountableSubtotal(lines: CartLine[]): number {
  return lines.reduce((acc, l) => {
    if (!isLineDiscountEligible(l)) return acc;
    return acc + l.quantity * cartLinePrice(l);
  }, 0);
}

